import { useEffect, useRef, useState } from "react";
import { Markdown } from "../Markdown";
import { Text } from "../Text";
import { MarkdownStyles } from "./MarkdownStyles";

type Props = {
  text: string;
  /** True while the entry is being actively streamed via SSE. */
  streaming?: boolean;
  onAnimatingChange?: (animating: boolean) => void;
  /** Render plain text instead of Markdown. */
  plain?: boolean;
  variant?: "text-regular" | "text-small";
  color?: string;
  resolveFileHref?: (href: string) => string | null;
};

const MS_PER_CHAR = 5;

export function TypewriterText({
  text,
  streaming,
  onAnimatingChange,
  plain,
  variant = "text-regular",
  color,
  resolveFileHref,
}: Props) {
  const [revealed, setRevealed] = useState(() => text.length);
  const revealedRef = useRef(text.length);
  const textRef = useRef(text);
  const animRef = useRef<number | null>(null);
  const lastFrameRef = useRef(0);
  const onAnimChangeRef = useRef(onAnimatingChange);
  const streamingRef = useRef(streaming);
  const mountedWithTextRef = useRef(text.length > 0);

  textRef.current = text;
  onAnimChangeRef.current = onAnimatingChange;
  streamingRef.current = streaming;

  useEffect(() => {
    if (revealedRef.current >= text.length) {
      if (!streamingRef.current) {
        if (animRef.current !== null) {
          cancelAnimationFrame(animRef.current);
          animRef.current = null;
          lastFrameRef.current = 0;
          onAnimChangeRef.current?.(false);
        }
      }
      return;
    }

    // Large jump on mount (page refresh / Zero sync) — snap immediately
    if (!mountedWithTextRef.current) {
      const jump = text.length - revealedRef.current;
      if (jump >= 30) {
        revealedRef.current = text.length;
        setRevealed(text.length);
        mountedWithTextRef.current = true;
        return;
      }
    }
    mountedWithTextRef.current = true;

    if (animRef.current !== null) return;

    onAnimChangeRef.current?.(true);

    const tick = (time: number) => {
      if (lastFrameRef.current === 0) lastFrameRef.current = time;
      const chars = Math.floor((time - lastFrameRef.current) / MS_PER_CHAR);
      if (chars > 0) {
        const next = Math.min(
          revealedRef.current + chars,
          textRef.current.length
        );
        revealedRef.current = next;
        setRevealed(next);
        lastFrameRef.current = time;
      }

      // Keep the animation loop running while streaming, even if caught up.
      // This prevents the start-stop-start pattern that causes choppiness.
      if (
        revealedRef.current < textRef.current.length ||
        streamingRef.current
      ) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        animRef.current = null;
        lastFrameRef.current = 0;
        onAnimChangeRef.current?.(false);
      }
    };

    animRef.current = requestAnimationFrame(tick);
  }, [text]);

  // When streaming ends, snap to full text
  useEffect(() => {
    if (streaming === false && revealedRef.current < textRef.current.length) {
      if (animRef.current !== null) {
        cancelAnimationFrame(animRef.current);
        animRef.current = null;
        lastFrameRef.current = 0;
      }
      revealedRef.current = textRef.current.length;
      setRevealed(textRef.current.length);
      onAnimChangeRef.current?.(false);
    }
  }, [streaming]);

  useEffect(() => {
    return () => {
      if (animRef.current !== null) {
        cancelAnimationFrame(animRef.current);
        onAnimChangeRef.current?.(false);
      }
    };
  }, []);

  const sliced = text.slice(0, revealed);

  return (
    <Text variant={variant} style={{ minWidth: 0 }} color={color}>
      {plain ? (
        <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {sliced}
        </span>
      ) : (
        <MarkdownStyles>
          <Markdown internalHref={resolveFileHref}>{sliced}</Markdown>
        </MarkdownStyles>
      )}
    </Text>
  );
}
